// ACTIONS: the archived spaces and boards at the foot of the Work sidebar, each with a visible
// Restore icon (`POST spaces/{id}/unarchive`, `POST boards/{id}/unarchive`). A space comes back
// with exactly the channels and boards its archive took; a board with its home channel. A board
// of an archived space is restored with its space, so only the space is offered then. The list
// is folded under "Archived" (members only) and read when unfolded; the API decides who may
// restore (the space's owners, the board's editors) and says so when it refuses.
import { cn, toast } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveIcon, ChevronRightIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isMember } from "../company";
import { RowAction } from "../settings/row-actions";
import { WORK_PAGE, workKeys } from "./work-queries";

const archivedQuery = (api: Api, co: string) =>
  queryOptions({
    // Under the spaces key: an archive (which refreshes the spaces) refreshes this list too.
    queryKey: [...workKeys.spaces(co), "archived"] as const,
    refetchOnMount: "always",
    queryFn: async () => {
      const q = { limit: WORK_PAGE, include_archived: "true" as const };
      const [spaces, boards] = await Promise.all([
        api.client.spaces.list({ params: { co }, query: q }),
        api.client.boards.list({ params: { co }, query: q }),
      ]);
      const gone = spaces.data.filter((s) => s.archived_at);
      const goneIds = new Set(gone.map((s) => s.id));
      return {
        spaces: gone,
        // A board of an archived space comes back with its space.
        boards: boards.data.filter((b) => b.archived_at && !goneIds.has(b.space_id)),
      };
    },
  });

export function ArchivedWork({
  api,
  company,
  enabled,
}: {
  api: Api;
  company: CompanyAccess;
  enabled: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // Read only once unfolded: the sidebar's calls on load stay as they were.
  const q = useQuery({ ...archivedQuery(api, co), enabled: enabled && open });
  const restore = useMutation({
    mutationFn: async (x: { kind: "space" | "board"; id: string; name: string }) => {
      if (x.kind === "space") await api.client.spaces.unarchive({ params: { co, id: x.id } });
      else await api.client.boards.unarchive({ params: { co, id: x.id } });
    },
    onSuccess: (_d, x) => toast.add({ title: `${x.name} restored`, type: "success" }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: workKeys.spaces(co) }),
        qc.invalidateQueries({ queryKey: workKeys.boards(co) }),
      ]),
  });
  const rows = [
    ...(q.data?.spaces ?? []).map((s) => ({ kind: "space" as const, id: s.id, name: s.name })),
    ...(q.data?.boards ?? []).map((b) => ({ kind: "board" as const, id: b.id, name: b.name })),
  ];
  if (!enabled || !isMember(company)) return null;
  return (
    <div data-slot="archived-work" className="mx-2 mt-2">
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-3.5 transition-transform", open && "rotate-90")}
        />
        <ArchiveIcon aria-hidden="true" className="size-3.5" />
        Archived{q.data ? ` (${rows.length})` : ""}
      </button>
      {open && q.data && !rows.length ? (
        <p className="py-1 pl-7 text-xs text-muted-foreground">Nothing archived.</p>
      ) : null}
      {open && rows.length ? (
        <ul aria-label="Archived spaces and boards" className="m-0 flex list-none flex-col p-0">
          {rows.map((r) => (
            <li
              key={r.id}
              data-archived={r.kind}
              className="flex min-h-8 items-center gap-2 rounded-md pl-7 pr-1 text-sm text-muted-foreground hover:bg-muted"
            >
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              <span className="text-2xs text-faint">{r.kind}</span>
              <RowAction
                icon={RotateCcwIcon}
                label={`Restore ${r.name}`}
                tip="Restore"
                disabled={restore.isPending}
                onClick={() => restore.mutate(r)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
